import { useEffect, useState } from 'react'
import { z } from 'zod'

import { parseCursorPage } from '@/shared/api/paging'
import { useApiClient } from '@/shared/api/use-api-client'

import type { FilterSuggestions } from './definition'
import { matchesAt } from './highlight'

/** How long typing settles before suggestions are asked for. */
export const suggestionDelayMs = 250

/** The most suggestions a field lists. */
export const maxSuggestions = 8

// Enough records that a few whose other fields matched, such as a person's address, still leave a full list.
const requested = 25

// The most pages read for one text, so a common one never walks a whole list.
const maxPages = 3

const named = z.object({ name: z.string() })

function nameOf(record: unknown): string | null {
  const parsed = named.safeParse(record)
  return parsed.success ? parsed.data.name : null
}

const order = new Intl.Collator(undefined, { sensitivity: 'base' })

function contains(value: string, term: string): boolean {
  for (let at = 0; at <= value.length - term.length; at += 1) {
    if (matchesAt(value, at, term)) {
      return true
    }
  }
  return false
}

/**
 * The distinct values that hold the text, those that start with it first, each group in
 * alphabetical order, at most {@link maxSuggestions}.
 */
export function rankSuggestions(values: readonly (string | null)[], text: string): readonly string[] {
  const term = text.trim()
  const seen = new Set<string>()
  const kept: string[] = []
  for (const value of values) {
    const shown = value?.trim() ?? ''
    const key = shown.toLocaleLowerCase()
    if (shown !== '' && !seen.has(key) && contains(shown, term)) {
      seen.add(key)
      kept.push(shown)
    }
  }
  const starts = (value: string) => matchesAt(value, 0, term)
  return kept
    .toSorted((a, b) => Number(starts(b)) - Number(starts(a)) || order.compare(a, b))
    .slice(0, maxSuggestions)
}

export type SuggestionsState =
  | { readonly status: 'idle' | 'loading' | 'error' }
  | { readonly status: 'ready'; readonly values: readonly string[] }

type Answer = { readonly key: string } & ({ readonly failed: true } | { readonly failed: false; readonly values: readonly string[] })

/**
 * The values `source` suggests for `text`, once typing settles; `idle` with no text. A newer
 * text cancels the request before it, so an older answer never replaces a newer one.
 */
export function useSuggestions(source: FilterSuggestions, text: string, includeInactive: boolean): SuggestionsState {
  const client = useApiClient()
  const term = text.trim()
  const inactive = includeInactive && source.hasInactive === true
  const key = JSON.stringify([source.path, term, inactive])
  const [answer, setAnswer] = useState<Answer | null>(null)
  const { path, valueFrom = nameOf } = source

  useEffect(() => {
    if (term === '') {
      return undefined
    }
    const controller = new AbortController()
    const load = async () => {
      const query = new URLSearchParams({ q: term, limit: String(requested) })
      if (inactive) {
        query.set('includeInactive', 'true')
      }
      try {
        // A list whose `q` also matches other fields, such as a person's address, can fill a page
        // with records whose value lacks the text: read on, a few pages at most, for a full list.
        const values: (string | null)[] = []
        let cursor: string | null = null
        for (let read = 0; read < maxPages; read += 1) {
          if (cursor !== null) {
            query.set('cursor', cursor)
          }
          const page = parseCursorPage(await client.get(`${path}?${query.toString()}`, controller.signal), (record) => valueFrom(record))
          values.push(...page.data)
          cursor = page.pagination.hasMore ? page.pagination.nextCursor : null
          if (cursor === null || rankSuggestions(values, term).length >= maxSuggestions) {
            break
          }
        }
        setAnswer({ key, failed: false, values: rankSuggestions(values, term) })
      } catch {
        // The client has logged it; the field still takes typed text.
        if (!controller.signal.aborted) {
          setAnswer({ key, failed: true })
        }
      }
    }
    const timer = setTimeout(() => void load(), suggestionDelayMs)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [client, key, path, term, inactive, valueFrom])

  if (term === '') {
    return { status: 'idle' }
  }
  if (answer?.key !== key) {
    return { status: 'loading' }
  }
  return answer.failed ? { status: 'error' } : { status: 'ready', values: answer.values }
}
